import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  ColorInput,
  Container,
  Group,
  Loader,
  Modal,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { AppLayout } from '../features/projects/app-shell'
import { useAuthStore } from '../features/auth/store'
import {
  createFunnelSegment,
  deleteFunnelSegment,
  fetchFunnelSegments,
  updateFunnelSegment,
  type FunnelSegmentRow,
} from '../shared/api/funnel-segments'

const SWATCHES = ['#2f9e44', '#f08c00', '#1971c2', '#868e96', '#e03131', '#9c36b5', '#0c8599', '#5c940d']

export function AdminSegmentsPage() {
  const { user } = useAuthStore()
  const [segments, setSegments] = useState<FunnelSegmentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<FunnelSegmentRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<FunnelSegmentRow | null>(null)

  async function reload() {
    setLoading(true)
    try {
      setSegments(await fetchFunnelSegments())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  if (!user) return null
  if (user.role !== 'admin') {
    return (
      <AppLayout>
        <Container>
          <Text c="red" mt="md">Доступ только для admin.</Text>
        </Container>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <Container size="md" py="md">
        <Group justify="space-between" mb="md">
          <Title order={3}>Сегменты воронки</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            Добавить сегмент
          </Button>
        </Group>
        <Text c="dimmed" size="sm" mb="md">
          Справочник «температуры» клиента для будущих рассылок. Сегмент назначается проекту
          на вкладке «Обзор».
        </Text>

        {loading ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Название</Table.Th>
                <Table.Th>Цвет</Table.Th>
                <Table.Th w={100}>Порядок</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {segments.map((s) => (
                <Table.Tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => setEditing(s)}>
                  <Table.Td><Text size="sm">{s.name}</Text></Table.Td>
                  <Table.Td>
                    <Badge style={{ background: s.color, color: '#fff' }}>{s.color}</Badge>
                  </Table.Td>
                  <Table.Td>{s.order}</Table.Td>
                  <Table.Td>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      onClick={(e) => { e.stopPropagation(); setDeleting(s) }}
                    >
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {segments.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">Сегментов пока нет.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}

        <SegmentModal
          opened={creating}
          segment={null}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); void reload() }}
        />
        <SegmentModal
          opened={!!editing}
          segment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void reload() }}
        />

        <Modal
          opened={!!deleting}
          onClose={() => setDeleting(null)}
          title={deleting ? `Удалить «${deleting.name}»?` : ''}
          centered
        >
          {deleting && (
            <Stack>
              <Text size="sm">
                Сегмент исчезнет из фильтров и форм. Если он назначен хотя бы одному проекту —
                удаление будет заблокировано.
              </Text>
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setDeleting(null)}>Отмена</Button>
                <Button
                  color="red"
                  onClick={async () => {
                    try {
                      await deleteFunnelSegment(deleting.id)
                      notifications.show({ message: 'Сегмент удалён', color: 'green' })
                      setDeleting(null)
                      void reload()
                    } catch (e) {
                      const status = (e as { response?: { status?: number } })?.response?.status
                      notifications.show({
                        message:
                          status === 409
                            ? 'Сегмент нельзя удалить: он назначен проектам. Сначала переназначьте их.'
                            : extractError(e),
                        color: 'red',
                      })
                    }
                  }}
                >
                  Удалить
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Container>
    </AppLayout>
  )
}

function SegmentModal({
  opened,
  segment,
  onClose,
  onSaved,
}: {
  opened: boolean
  segment: FunnelSegmentRow | null
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState('')
  const [color, setColor] = useState('#868e96')
  const [order, setOrder] = useState<number | ''>(0)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (opened) {
      setName(segment?.name ?? '')
      setColor(segment?.color ?? '#868e96')
      setOrder(segment?.order ?? 0)
    }
  }, [opened, segment])

  const colorValid = /^#[0-9a-fA-F]{6}$/.test(color)
  const canSave = name.trim().length >= 1 && name.trim().length <= 120 && colorValid

  async function save() {
    setSaving(true)
    try {
      const payload = { name: name.trim(), color, order: order === '' ? 0 : order }
      if (segment) await updateFunnelSegment(segment.id, payload)
      else await createFunnelSegment(payload)
      notifications.show({ message: segment ? 'Сохранено' : 'Сегмент создан', color: 'green' })
      onSaved()
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status
      notifications.show({
        message: status === 409 ? 'Сегмент с таким названием уже есть.' : extractError(e),
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={segment ? `Редактирование: ${segment.name}` : 'Новый сегмент'}
      centered
    >
      <Stack>
        <TextInput
          label="Название"
          required
          placeholder="тёплый"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <ColorInput
          label="Цвет"
          format="hex"
          swatches={SWATCHES}
          value={color}
          onChange={setColor}
        />
        <NumberInput
          label="Порядок"
          min={0}
          value={order}
          onChange={(v) => setOrder(typeof v === 'number' ? v : '')}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Отмена</Button>
          <Button loading={saving} disabled={!canSave} onClick={save}>
            {segment ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string; code?: string } } } }
  return err?.response?.data?.error?.message ?? err?.response?.data?.error?.code ?? 'Ошибка'
}
